import { Backdrop, Box, Skeleton, useTheme, Grid2 } from '@mui/material'
import { FC, ReactElement, useEffect } from 'react'

import MoovyErrorPage from '../moovyErrorPage'
import MoovyMutationError from '../moovyMutationError'
import MoovyProgress from '../moovyProgress'
import { SS_APP_BACKDROP_ANIMATION } from '../../hooks/globalKeys'
import { UseQueryResult } from '@tanstack/react-query'
import { navigateToRootIfNotFoundError } from '../../utils/errorNavigation'
import useGlobalUI from '../../hooks/useGlobalUI'
import { useNavigate } from 'react-router'

type MoovyQueryHandlerProps = {
  query: UseQueryResult<any, any>
  rootPageIfNotFoundError: boolean
  children: any
}

const BackdropAnimation = ({ show }: { show: boolean }) => {
  const theme = useTheme()
  const [state, setState] = useGlobalUI({
    key: SS_APP_BACKDROP_ANIMATION,
    initialValue: { value: '' }
  })

  useEffect(() => {
    if (show != !!state.value) {
      setState({ value: show ? 'true' : '' })
    }
  }, [show])

  return (
    <Box
      sx={{
        opacity: 0.5,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: theme.zIndex.drawer + 1,
        visibility: show ? 'visible' : 'hidden'
      }}
    >
      <Backdrop
        sx={{
          backgroundColor: theme.palette.customColors.mainBackground
        }}
        open={show}
      >
        <MoovyProgress />
      </Backdrop>
    </Box>
  )
}

const ProgressAnimation = () => {
  const getSkeletonAnimations = () => {
    const skeletonCount = 8
    const skeletonItems = []
    for (let i = 0; i < skeletonCount; i++) {
      skeletonItems.push(<Skeleton key={i} animation="wave" />)
    }
    return skeletonItems.map((item) => item)
  }

  return (
    <Grid2 container gap={2} mt={2}>
      <Grid2 size={12}>
        <Skeleton variant="rounded" height={60} />
      </Grid2>
      <Grid2 size={12}>
        {getSkeletonAnimations()}
      </Grid2>
    </Grid2>
  )
}

const MoovyQueryHandler: FC<MoovyQueryHandlerProps> = ({
  query,
  rootPageIfNotFoundError,
  children
}): ReactElement => {
  const navigate = useNavigate()
  if (rootPageIfNotFoundError) {
    navigateToRootIfNotFoundError(navigate, query.error)
  }

  const noDataAnimation = query.isFetching && !query.data
  const dataAnimation = query.isFetching && !!query.data

  return (
    <>
      {query.isError ? (
        <MoovyErrorPage />
      ) : noDataAnimation ? (
        <ProgressAnimation />
      ) : (
        <>
          <BackdropAnimation show={dataAnimation} />
          <>{children}</>
        </>
      )}
      <MoovyMutationError
        query={query}
        errorTitleKey={'common.actions.get.error'}
      />
    </>
  )
}

export default MoovyQueryHandler
